import { faAdd } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button } from '@mui/material';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import LoadingIndicator from '@/components/LoadingIndicator';
import ResizablePanel from '@/components/ResizablePanel';
import PERMISSIONS from '@/constants/auth.constants';
import PcapRequestCreateDialog from '@/features/PcapRequestCreateDialog';
import PcapRequestDetails from '@/features/PcapRequestDetails/PcapRequestDetails';
import PcapRequestsTable from '@/features/PcapRequestsTable/PcapRequestsTable';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import { administrationActionsAtom } from '../Administration.store';
import {
  requestDetailsExpandedAtom,
  useFetchRequests,
  useFetchSensorsList
} from './AdministrationPcapRequests.store';

const AdministrationPcapRequests = () => {
  const [requestDetailsExpanded, setRequestDetailsExpanded] = useAtom(
    requestDetailsExpandedAtom
  );
  const [selectedOrg] = useAtom(selectedOrganizationAtom);
  const [open, setOpen] = useState(false);
  const [, setPageActions] = useAtom(administrationActionsAtom);
  const userCanCreate = usePermissionsCheck(PERMISSIONS.PCAPS_CREATE);

  const { isFetching: isLoadingRequests } = useFetchRequests();
  const { isFetching: isLoadingSensors } = useFetchSensorsList();
  const isLoading = isLoadingRequests || isLoadingSensors;

  const sizes = ['75%', '25%'];

  useEffect(() => {
    // Collapse the details pane when the selected organization changes
    if (requestDetailsExpanded) {
      setRequestDetailsExpanded(false);
    }
  }, [selectedOrg]);

  const panels = useMemo(
    () => [
      {
        id: 'requests-table',
        content: <PcapRequestsTable />,
        order: 1,
        minSize: requestDetailsExpanded ? '40%' : '100%',
        maxSize: requestDetailsExpanded ? '80%' : '100%',
        showResizeHandle: true // Always show handle since we always have 2 panels
      },
      {
        id: 'request-details',
        content: <PcapRequestDetails />,
        order: 2,
        minSize: requestDetailsExpanded ? '20%' : '0%',
        maxSize: requestDetailsExpanded ? '40%' : '0%',
        showResizeHandle: false
      }
    ],
    [requestDetailsExpanded]
  );

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setPageActions(() =>
      userCanCreate ? (
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpen(true)}
          startIcon={<FontAwesomeIcon icon={faAdd} size="1x" />}
        >
          Create Request
        </Button>
      ) : null
    );
    return () => {
      setPageActions(null);
    };
  }, [userCanCreate]);

  return (
    <Box sx={{ height: '100%' }}>
      {open && <PcapRequestCreateDialog open={open} onClose={handleClose} />}
      {isLoading && <LoadingIndicator isLoading={isLoading} />}
      <ResizablePanel sizes={sizes} panels={panels} />
    </Box>
  );
};

export default AdministrationPcapRequests;
